import React from 'react';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';
import { EMPLOYMENT_TYPE_OPTIONS } from '../utils/constants';
import { formatDaysHalf } from '../utils/leaveFormat';
import { CISelect } from './CISelect';
import styles from './ContractHistoryTimeline.module.css';

const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

interface NewContractFormProps {
  newPeriod: {
    start_date: string;
    position_title: string;
    employment_type: string;
    work_area: string;
    hours_per_week: string;
    days_distribution: string[];
    regular_workdays: number[];
    contract_end_date: string;
    probation_months: string;
    extra_leave_days: string;
    comp_type: 'HOURLY' | 'SALARY';
    comp_amount: string;
  };
  onNewPeriodChange: (period: any) => void;
  workAreas: Array<{ id: number; name: string }>;
  positionTitle: string;
  hoursPerWeek: number | null;
  startDate: string | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  startDateInputRef: React.RefObject<HTMLDivElement>;
  workAreaInputRef: React.RefObject<HTMLDivElement>;
}

export const NewContractForm: React.FC<NewContractFormProps> = ({
  newPeriod,
  onNewPeriodChange,
  workAreas,
  positionTitle,
  hoursPerWeek,
  startDate,
  saving,
  onSave,
  onCancel,
  startDateInputRef,
  workAreaInputRef,
}) => {
  const wdCount = newPeriod.regular_workdays.filter(v => v > 0).length;
  const newStatutory = wdCount > 0 ? wdCount * 4 : null;
  const newExtra = newPeriod.extra_leave_days ? parseFloat(newPeriod.extra_leave_days) : 0;
  const newTotal = newStatutory != null ? newStatutory + newExtra : null;

  return (
    <div className={styles.newFormCard}>
      <div className={styles.newFormTitle}>
        {startDate ? 'New Contract' : 'Vertragsdaten ergaenzen'}
      </div>
      <div className={styles.formColumn}>
        {/* Row 1: Start + End */}
        <div className={styles.formGrid2}>
          <div ref={startDateInputRef} className="pa-field" style={{ ...ps.fieldWrapper, borderRadius: 8 }}>
            <label style={ps.labelStyle}>{startDate ? 'Vertragsbeginn' : 'Startdatum'}</label>
            <input type="date" style={ps.inputStyle} value={newPeriod.start_date} onChange={e => onNewPeriodChange({ ...newPeriod, start_date: e.target.value })} />
          </div>
          <div className="pa-field" style={ps.fieldWrapper}>
            <label style={ps.labelStyle}>Vertragsende (optional)</label>
            <input type="date" style={ps.inputStyle} value={newPeriod.contract_end_date} onChange={e => onNewPeriodChange({ ...newPeriod, contract_end_date: e.target.value })} />
          </div>
        </div>

        {/* Row 2: Rolle + Probezeit */}
        <div className={styles.formGrid2}>
          <div className="pa-field" style={ps.fieldWrapper}>
            <label style={ps.labelStyle}>Rolle</label>
            <input type="text" style={ps.inputStyle} value={newPeriod.position_title} onChange={e => onNewPeriodChange({ ...newPeriod, position_title: e.target.value })} onFocus={e => e.target.select()} placeholder={positionTitle !== '\u2013' ? positionTitle : ''} />
          </div>
          <div className="pa-field" style={ps.fieldWrapper}>
            <label style={ps.labelStyle}>Probezeit (Monate)</label>
            <CISelect
              value={newPeriod.probation_months}
              onChange={val => onNewPeriodChange({ ...newPeriod, probation_months: val })}
              options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1} ${i + 1 === 1 ? 'Monat' : 'Monate'}` }))}
              placeholder="Keine Probezeit"
            />
          </div>
        </div>

        {/* Row 3: Arbeitsbereich + Beschaeftigungsart */}
        <div className={styles.formGrid2}>
          <div ref={workAreaInputRef} className="pa-field" style={{ ...ps.fieldWrapper, borderRadius: 8 }}>
            <label style={ps.labelStyle}>Arbeitsbereich</label>
            <CISelect value={newPeriod.work_area} onChange={val => onNewPeriodChange({ ...newPeriod, work_area: val })} options={workAreas.map(wa => ({ value: String(wa.id), label: wa.name }))} />
          </div>
          <div className="pa-field" style={ps.fieldWrapper}>
            <label style={ps.labelStyle}>Beschaeftigungsart</label>
            <CISelect value={newPeriod.employment_type} onChange={val => onNewPeriodChange({ ...newPeriod, employment_type: val })} options={[...EMPLOYMENT_TYPE_OPTIONS]} />
          </div>
        </div>

        {/* Row 4: Stunden/Woche */}
        <div className="pa-field" style={ps.fieldWrapper}>
          <label style={ps.labelStyle}>Stunden/Woche</label>
          <input type="number" style={ps.inputStyle} value={newPeriod.hours_per_week} onChange={e => onNewPeriodChange({ ...newPeriod, hours_per_week: e.target.value })} onFocus={e => e.target.select()} placeholder={hoursPerWeek?.toString() || ''} step="0.5" min="0" max="48" />
        </div>

        {/* Row 5: Arbeitstage toggles */}
        <div className="pa-field" style={ps.fieldWrapper}>
          <label style={ps.labelStyle}>Arbeitstage</label>
          <div className={styles.dayToggles}>
            {DAY_LABELS.map((day, i) => {
              const active = newPeriod.regular_workdays[i] > 0;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    const updated = [...newPeriod.regular_workdays];
                    updated[i] = active ? 0 : 1;
                    onNewPeriodChange({ ...newPeriod, regular_workdays: updated });
                  }}
                  className={`${styles.dayToggle} ${active ? styles.dayToggleActive : styles.dayToggleInactive}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {wdCount > 0 && <div className={styles.dayCount}>{wdCount} Tage/Woche</div>}
        </div>

        {/* Row 6: Leave stats */}
        <div className={styles.formGrid3}>
          <div className="pa-field" style={{ ...ps.fieldWrapper, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <label style={ps.labelStyle}>Gesetzlicher Mindesturlaub</label>
            <div className={styles.statValue}>{newStatutory != null ? `${newStatutory} Tage` : '\u2013'}</div>
          </div>
          <div className="pa-field" style={ps.fieldWrapper}>
            <label style={ps.labelStyle}>Extra Urlaubstage</label>
            <input type="number" style={ps.inputStyle} value={newPeriod.extra_leave_days} onChange={e => onNewPeriodChange({ ...newPeriod, extra_leave_days: e.target.value })} onFocus={e => e.target.select()} placeholder="0" step="0.5" min="0" max="30" />
          </div>
          <div className="pa-field" style={{ ...ps.fieldWrapper, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <label style={ps.labelStyle}>Gesamt/Jahr</label>
            <div className={styles.statValueMagenta}>{newTotal != null ? `${formatDaysHalf(newTotal)} Tage` : '\u2013'}</div>
          </div>
        </div>
        {newTotal != null && (
          <div className={styles.leaveCalc}>= {newStatutory ?? 0} gesetzl. + {newExtra} extra</div>
        )}

        {/* Row 7: Verguetung */}
        <div className={styles.formGrid2}>
          <div className="pa-field" style={ps.fieldWrapper}>
            <label style={ps.labelStyle}>Verguetungsart</label>
            <div className={styles.compTypeContainer}>
              <button type="button" onClick={() => onNewPeriodChange({ ...newPeriod, comp_type: 'HOURLY' })} className={`${styles.compTypeButton} ${newPeriod.comp_type === 'HOURLY' ? styles.compTypeActive : styles.compTypeInactive}`}>
                Stundenlohn
              </button>
              <button type="button" onClick={() => onNewPeriodChange({ ...newPeriod, comp_type: 'SALARY' })} className={`${styles.compTypeButton} ${newPeriod.comp_type === 'SALARY' ? styles.compTypeActive : styles.compTypeInactive}`}>
                Gehalt
              </button>
            </div>
          </div>
          <div className="pa-field" style={ps.fieldWrapper}>
            <label style={ps.labelStyle}>
              {newPeriod.comp_type === 'HOURLY' ? 'Stundenlohn (EUR)' : 'Monatsgehalt (EUR)'}
            </label>
            <input
              type="text"
              inputMode="decimal"
              style={ps.inputStyle}
              value={newPeriod.comp_amount}
              onChange={e => onNewPeriodChange({ ...newPeriod, comp_amount: e.target.value })}
              onFocus={e => e.target.select()}
              onBlur={() => {
                const raw = newPeriod.comp_amount.replace(',', '.').trim();
                const num = parseFloat(raw);
                if (!isNaN(num)) {
                  onNewPeriodChange({ ...newPeriod, comp_amount: num.toFixed(2).replace('.', ',') });
                }
              }}
              placeholder={newPeriod.comp_type === 'HOURLY' ? '14,00' : '3.200,00'}
            />
          </div>
        </div>
      </div>
      <div className={styles.saveRow}>
        {startDate && (
          <button type="button" className="hover-btn-secondary" onClick={onCancel} style={{ ...ps.buttonSecondary, fontSize: 12, padding: '6px 16px' }}>
            Abbrechen
          </button>
        )}
        <button
          type="button"
          className="hover-btn-primary"
          onClick={onSave}
          disabled={saving}
          style={{ ...ps.buttonPrimary, fontSize: 12, padding: '6px 16px', opacity: saving ? 0.6 : 1 }}
        >
          {saving ? 'Speichere...' : 'Anlegen'}
        </button>
      </div>
    </div>
  );
};
